import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../components/dashboard/DashboardIcons';
import { parentNavigation, getParentRouteTitle } from '../config/parentNavigation';
import { useTasyarBrand } from '../lib/tasyarBrand';
import { useParentData } from '../context/ParentDataContext';

const isActive = (pathname, href) => pathname === href || (href !== '/parent' && pathname.startsWith(href + '/'));

export default function ParentLayout({ children }) {
  const brand = useTasyarBrand();
  const location = useLocation();
  const navigate = useNavigate();
  const pageTitle = getParentRouteTitle(location.pathname);
  const displayName = JSON.parse(localStorage.getItem('user') || '{}')?.full_name || 'ولي الأمر';
  const parentData = useParentData();
  const childrenCount = parentData?.childrenList?.length || 0;
  const unreadCount = parentData?.unreadCount || 0;

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.replace('/login');
  };

  const badgeFor = (item) => {
    if (item.href === '/parent/children' && childrenCount > 0) return childrenCount;
    if (item.href === '/parent/account' && unreadCount > 0) return unreadCount;
    return 0;
  };

  return (
    <div dir="rtl" className="min-h-screen bg-[#F8FAFC]" style={{ fontFamily: "'Noto Sans Arabic', Arial, sans-serif" }} data-testid="parent-shell">
      {/* شريط علوي زجاجي */}
      <header className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/80 backdrop-blur-md" data-testid="parent-topbar">
        <div className="mx-auto flex h-16 max-w-5xl items-center gap-3 px-4 sm:px-6">
          {brand.iconUrl ? (
            <img src={brand.iconUrl} alt="تسيار" className="h-9 w-9 rounded-xl object-contain" data-testid="parent-topbar-brand-icon" />
          ) : (
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-600 text-white"><Icon name="whistle" className="w-4.5 h-4.5" /></div>
          )}
          <div>
            <div className="text-sm font-extrabold text-slate-900" data-testid="parent-topbar-page-title">{pageTitle}</div>
            <div className="text-[11px] text-teal-600 font-semibold">بوابة ولي الأمر</div>
          </div>

          {/* تنقل علوي — كمبيوتر فقط */}
          <nav className="mr-auto hidden items-center gap-1 lg:flex" data-testid="parent-desktop-nav">
            {parentNavigation.map((item) => {
              const active = isActive(location.pathname, item.href);
              const badge = badgeFor(item);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  data-testid={`parent-nav-desktop-${item.href.replace(/\//g, '-')}`}
                  className={`relative flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-bold transition-all duration-200 ${active ? 'bg-teal-50 text-teal-700 shadow-sm' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'}`}
                >
                  <Icon name={item.icon} className="h-4 w-4" />
                  {item.name}
                  {badge > 0 && <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-bold text-white">{badge}</span>}
                </Link>
              );
            })}
          </nav>

          <div className="mr-auto flex items-center gap-3 lg:mr-0">
            <span className="hidden text-sm font-semibold text-slate-600 sm:block" data-testid="parent-topbar-user-name">{displayName}</span>
            <button onClick={handleLogout} data-testid="parent-logout-btn" className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600" title="تسجيل الخروج">
              <Icon name="logout" className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6 pb-32 sm:px-6 lg:pb-10" data-testid="parent-main-content">
        {children}
      </main>

      {/* قائمة سفلية عائمة — جوال فقط */}
      <nav
        className="fixed inset-x-0 bottom-0 z-40 mx-auto mb-2 flex max-w-md items-center justify-around rounded-2xl border border-slate-200/80 bg-white/90 px-2 py-2 shadow-lg backdrop-blur-xl lg:hidden"
        style={{ marginBottom: 'calc(env(safe-area-inset-bottom) + 8px)' }}
        data-testid="parent-bottom-nav"
      >
        {parentNavigation.map((item) => {
          const active = isActive(location.pathname, item.href);
          const badge = badgeFor(item);
          return (
            <button
              key={item.href}
              onClick={() => navigate(item.href)}
              data-testid={`parent-nav-mobile-${item.href.replace(/\//g, '-')}`}
              className={`relative flex flex-1 flex-col items-center gap-0.5 rounded-xl px-2 py-1.5 text-[10.5px] font-bold transition-all duration-200 ${active ? 'bg-teal-50 text-teal-600 shadow-sm' : 'text-slate-400'}`}
            >
              <Icon name={item.icon} className={`h-5 w-5 ${active ? 'text-teal-600' : 'text-slate-400'}`} />
              {item.name}
              {badge > 0 && <span className="absolute right-1 top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-orange-500 px-1 text-[9px] font-bold text-white">{badge}</span>}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
